import styled from 'styled-components';
import Button from '../Button/Button';
import SymbolFormat from './SymbolFormat';
import theme from '../../../theme';

const HeroBannerContainer = styled.div`
  color: ${({ textColor }) => textColor};
  display: flex;
  align-items: center;
  justify-content: flex-start;

  padding-top: 24px;

  &.is-standalone-layout {
    padding: ${({ hasMultipleBg }) => (
      hasMultipleBg
        ? '24px 0'
        : '48px 0'
    )};
  }

  &.extension-landing {
    justify-content: center;
    border-radius: 0;
    background-color: ${(p) => p.backgroundColor};

    .content-section--hero-body {
      max-width: 471px;
    }
    
    .content-section--hero-image {
      max-width: 454px;
    }

    .heading,
    .subheading {
      text-align: center;
      
      @media (min-width: ${(p) => p.theme.minWidthBreakpoint.tablet}px) {
        text-align: left;
      }
    }
  }

  @media screen and (max-width: ${(p) => p.theme.breakpoint.tablet}px) {
    justify-content: center;
  }

  .content-section {
    padding: ${({ hasMultipleBg }) => (
      hasMultipleBg
        ? '24px'
        : '0'
    )};
    border-radius: 32px;

    background-color: ${(p) => p.backgroundColor};
  }

  .content-section--hero {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    gap: ${(p) => p.theme.unit(3)};

    @media screen and (min-width: ${(p) => p.theme.minWidthBreakpoint.tablet}px) {
      ${({ hasTabletVerticalImage, shouldReverseLayout }) => (
        hasTabletVerticalImage && (!shouldReverseLayout
          ? 'flex-direction: row;'
          : 'flex-direction: row-reverse;'
        )
      )}
    }
    @media screen and (min-width: ${(p) => p.theme.minWidthBreakpoint.desktop}px) {
      gap: ${(p) => p.theme.unit(5)};

      ${({ shouldReverseLayout }) => (
        !shouldReverseLayout
          ? 'flex-direction: row;'
          : 'flex-direction: row-reverse;'
      )}
    }
  }

  .content-section--hero-image {
    max-width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    border-radius: 8px;
    overflow: hidden;

    .hero-image--vertical-tablet {
      display: none;
    }

    @media screen and (min-width: ${(p) => p.theme.minWidthBreakpoint.tablet}px) {
      ${({ hasTabletVerticalImage }) => hasTabletVerticalImage && `
        flex-shrink: 0;
        max-width: 50%;

        .hero-image--vertical-tablet {
          display: block;
          max-height: 336px;
        }

        .hero-image {
          display: none;
        }
      `}
    }
    @media screen and (min-width: ${(p) => p.theme.minWidthBreakpoint.desktop}px) {
      max-width: 454px;

      .hero-image--vertical-tablet {
        display: none;
      }

      .hero-image {
        display: block;
      }
    }

    img {
      width: 100%;
      object-fit: contain;
    }
  }

  .content-section--hero-body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    h1 {
      ${(p) => p.theme.typographyRebrand.heading['3xl']}
      margin: 0;
      margin-bottom: ${(p) => p.theme.unit(2)};
    }
    p {
      ${(p) => p.theme.typographyRebrand.body.lg}
      margin: 0;
      margin-bottom: 24px
    }
    .buttons {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: 16px;
    }

    @media screen and (min-width: ${(p) => p.theme.minWidthBreakpoint.tablet}px) {
      max-width: 646px;

      .buttons {
        flex-direction: row;
        flex-wrap: wrap;

        .button {
          flex-shrink: 0;
          white-space: nowrap;

          ${({ hasTwoButtons }) => hasTwoButtons && 'flex-basis: calc(50% - 8px);'}
        }
      }
    }
  }
`;

const HeroBannerLabel = styled.div`
  display: flex;
  padding: ${(p) => p.theme.unit(0.5)} ${(p) => p.theme.unit(2)};
  background-color: ${(p) => p.theme.c.tag.bg.primary};

  margin-bottom: ${(p) => p.theme.unit(2)};

  border-radius: 1000px;

  color: ${(p) => p.theme.c.tag.text.primary};
  ${(p) => p.theme.typographyRebrand.heading.sm}
`;

const HeroBannerSection = (props) => {
  const {
    heroImageUrl,
    portraitTabletHeroImageUrl,
    heroImageAltText = '',
    heading,
    subheading,
    labelText,
    backgroundColor,
    textColor,
    classNames = '',
    buttons,
    shouldReverseLayout,
    hasMultipleBg = false,
  } = props;

  const hasTwoButtons = Boolean(buttons && buttons.length === 2);

  return (
    <HeroBannerContainer
      hasTabletVerticalImage={Boolean(portraitTabletHeroImageUrl)}
      backgroundColor={backgroundColor || theme.c.bg['feature-1']}
      textColor={textColor || theme.c.text.primary}
      hasTwoButtons={hasTwoButtons}
      shouldReverseLayout={shouldReverseLayout}
      hasMultipleBg={hasMultipleBg}
      className={classNames}
    >
      <div className="content-section content-section--hero">
        <div className="content-section--hero-image">
          <img
            className="hero-image"
            src={`https://${heroImageUrl}`}
            alt={heroImageAltText}
          />

          {portraitTabletHeroImageUrl && (
            <img
              className="hero-image--vertical-tablet"
              src={`https://${portraitTabletHeroImageUrl}`}
              alt={heroImageAltText}
            />
          )}
        </div>
        <div className="content-section--hero-body">
          {labelText && (
            <HeroBannerLabel>{labelText}</HeroBannerLabel>
          )}

          <SymbolFormat className="heading" elementTag="h1" text={heading} />

          {subheading && (
            <p className="subheading">{subheading}</p>
          )}

          {buttons && buttons.length && (
            <div className="buttons">
              {buttons.map((button) => (
                <Button
                  className="button"
                  {...button}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </HeroBannerContainer>
  );
};

export default HeroBannerSection;
